// src/contexts/LoadingContext.jsx
//
// Keeps your existing API exactly:
//   showLoading(msg?)  — call from axios interceptor for POST/PUT/DELETE only
//   hideLoading()      — call from axios interceptor on response/error
//   loading            — boolean, consumed by GlobalLoader
//   message            — string, consumed by GlobalLoader
//
// The only behaviour change: GlobalLoader no longer renders a fullscreen
// white overlay. It renders a slim top progress bar instead (see GlobalLoader.jsx).
// Your existing call-sites (showLoading / hideLoading) need zero changes.

import React, { createContext, useState, useContext, useRef, useCallback } from "react";

const LoadingContext = createContext();

export const LoadingProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Counter prevents concurrent requests from racing each other off.
  // Without this, if two mutations run at once and the first finishes,
  // hideLoading() would turn off the bar while the second is still running.
  const countRef = useRef(0);

  const showLoading = useCallback((msg = "") => {
    countRef.current += 1;
    if (msg) setMessage(msg);
    setLoading(true);
  }, []);

  const hideLoading = useCallback(() => {
    countRef.current = Math.max(0, countRef.current - 1);
    if (countRef.current === 0) {
      setLoading(false);
      setMessage("");
    }
  }, []);

  return (
    <LoadingContext.Provider value={{ loading, message, showLoading, hideLoading }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => useContext(LoadingContext);